/**
 * The monthly AI budget (D631, doc 25 §1 Q-362 and §11 "AI spend panel") — governed key `ai.monthlyBudget`.
 *
 * What the AI may cost in one calendar month, as an amount in ONE currency (integer minor units, ISO 4217) with the percentages at
 * which an alert is raised (default 80 and 100). Two kinds of bucket read side by side:
 *  - a COUNTRY budget — what that country's AI may cost (conversations, voice, QA, simulation run for it);
 *  - the PLATFORM total — the ceiling over every country (and over the AI that belongs to no country).
 * They are independent budgets, not an override: the spend panel judges a country's spend against its own budget AND the platform
 * total against the platform's. This module only says what each budget is and when an alert fires; it never blocks a call (the
 * panel, the alert and the owner decide what to do at 100 %).
 *
 * Money, so four eyes on every change; a settings-kernel key (D393): platform and country scope, OVERRIDABLE, versioned,
 * effective-dated, content-hashed. NO default: until the owner fills a budget there is none, and the panel says "sem orçamento"
 * instead of inventing one (`AI_BUDGET_UNSET`). An unreadable version fails closed as `AI_BUDGET_INVALID` (the panel shows the spend
 * with a warning, never a made-up ceiling). Neutral: no country or currency is named here.
 */
import { COUNTRY_RE, ISO4217_RE, effectiveBucket, isObj, onlyMembers, scopedSetting } from './central-ai-shared.js';
/** The governed key. */
export const AI_MONTHLY_BUDGET_KEY = 'ai.monthlyBudget';
/** The alert thresholds a budget starts with (percent of the budget spent). */
export const DEFAULT_AI_BUDGET_ALERT_PERCENTS = [80, 100];
export const AI_BUDGET_ALERT_BOUNDS = { minPercent: 1, maxPercent: 500, maxThresholds: 6 };
export const AI_MONTHLY_BUDGET_REASON_CODES = ['AI_BUDGET_RESOLVED', 'AI_BUDGET_UNSET', 'AI_BUDGET_INVALID'];
/** The stable issue of a value that is not a monthly budget, or undefined when it is one. */
export function aiMonthlyBudgetIssue(v) {
    if (!isObj(v))
        return 'NOT_AN_OBJECT';
    if (!onlyMembers(v, ['amountMinor', 'currency', 'alertPercents']) || !('amountMinor' in v) || !('currency' in v) || !('alertPercents' in v))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (typeof v.amountMinor !== 'number' || !Number.isSafeInteger(v.amountMinor) || v.amountMinor < 1)
        return 'INVALID_AMOUNT';
    if (typeof v.currency !== 'string' || !ISO4217_RE.test(v.currency))
        return 'INVALID_CURRENCY';
    if (!Array.isArray(v.alertPercents) || v.alertPercents.length < 1 || v.alertPercents.length > AI_BUDGET_ALERT_BOUNDS.maxThresholds)
        return 'INVALID_ALERT_PERCENTS';
    let prev = 0;
    for (const p of v.alertPercents) {
        if (typeof p !== 'number' || !Number.isInteger(p) || p < AI_BUDGET_ALERT_BOUNDS.minPercent || p > AI_BUDGET_ALERT_BOUNDS.maxPercent)
            return 'INVALID_ALERT_PERCENTS';
        if (p <= prev)
            return 'ALERT_PERCENTS_NOT_ASCENDING';
        prev = p;
    }
    return undefined;
}
export function validAiMonthlyBudgetValue(v) {
    return aiMonthlyBudgetIssue(v) === undefined;
}
/** Build a valid version (platform or country scope). Throws a RangeError on a malformed scope ref or value. */
export function aiMonthlyBudgetSetting(input) {
    const issue = aiMonthlyBudgetIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid AI monthly budget: ${issue}`);
    const value = { amountMinor: input.value.amountMinor, currency: input.value.currency, alertPercents: [...input.value.alertPercents] };
    return scopedSetting({ key: AI_MONTHLY_BUDGET_KEY, scope: input.scope, ...(input.scopeRef !== undefined ? { scopeRef: input.scopeRef } : {}), value, version: input.version, effectiveFrom: input.effectiveFrom, ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}), mode: 'OVERRIDABLE' });
}
/**
 * The budget of ONE bucket in force at `at`: the platform total (`{ scope: 'platform' }`) or one country's
 * (`{ scope: 'country', country }`). The two never inherit from each other.
 */
export function resolveAiMonthlyBudget(settings, bucket, at) {
    if (bucket.scope === 'country' && !COUNTRY_RE.test(bucket.country))
        return { ok: false, reasonCode: 'AI_BUDGET_INVALID' };
    const found = effectiveBucket(settings, AI_MONTHLY_BUDGET_KEY, bucket.scope, bucket.scope === 'country' ? bucket.country : undefined, at);
    if (found === undefined)
        return { ok: false, reasonCode: 'AI_BUDGET_UNSET' };
    if (!validAiMonthlyBudgetValue(found.value))
        return { ok: false, reasonCode: 'AI_BUDGET_INVALID' };
    return {
        ok: true,
        reasonCode: 'AI_BUDGET_RESOLVED',
        scope: bucket.scope,
        amountMinor: BigInt(found.value.amountMinor),
        currency: found.value.currency,
        alertPercents: [...found.value.alertPercents],
        policyVersion: found.policyVersion,
        ...(found.contentHash !== undefined ? { contentHash: found.contentHash } : {}),
        ...(found.changeId !== undefined ? { changeId: found.changeId } : {}),
    };
}
/** Where a month-to-date spend (minor units of the budget's currency) stands against the budget and its alert thresholds. */
export function aiBudgetStatus(budget, spentMinor) {
    const spent = spentMinor < 0n ? 0n : spentMinor;
    const usedBasisPoints = (spent * 10000n) / budget.amountMinor;
    const crossedPercents = budget.alertPercents.filter((p) => usedBasisPoints >= BigInt(p) * 100n);
    return { usedBasisPoints, crossedPercents, level: usedBasisPoints >= 10000n ? 'EXCEEDED' : crossedPercents.length > 0 ? 'ALERT' : 'OK' };
}
//# sourceMappingURL=ai-monthly-budget-policy.js.map